import React, { useState, useMemo, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { range } from 'utils/commonUtils'
import { OptionItem } from '../common'
import PullDownPicker from './PullDownPicker'
import Text from '../texts/Text'

// 変形フォーマットプラグインを追加
dayjs.extend(advancedFormat)

export type TimePickerProps = {
  /** 時間(HH:mm) */
  time: string
  /** 選択可能な時間の最小値(HH:mm) */
  minTime?: string
  /** 値が変更された時のコールバック */
  onChanged?: (time: string) => void
}

/** 時間を選択するコンポーネント */
const TimePicker = (props: TimePickerProps): JSX.Element => {
  const { time, minTime, onChanged } = props
  // 時間
  const [hour, minute] = useMemo(() => {
    const djTime = dayjs(`2000/1/1 ${time}`)
    return [djTime.hour(), djTime.minute()]
  }, [time])
  // 最小時間
  const [minHour, minMinute] = useMemo(() => {
    const djTime = minTime ? dayjs(`2000/1/1 ${minTime}`) : undefined
    return djTime ? [djTime.hour(), djTime.minute()] : [8, 0]
  }, [minTime])

  const [isPickerOpenedHour, setIsPickerOpenedHour] = useState(false)
  const [isPickerOpenedMinute, setIsPickerOpenedMinute] = useState(false)
  const [hourItems, setHourItems] = useState<OptionItem<number>[]>([])
  const [minuteItems, setMinuteItems] = useState<OptionItem<number>[]>([])
  const [selectedHour, setSelectedHour] = useState(hour)
  const [selectedMinute, setSelectedMinute] = useState(minute)

  // 時間の選択肢を生成（ 8:00 - 18:00まで ）
  const hourOptions: OptionItem<number>[] = range(8, 18).map((value) => ({
    label: String(value).padStart(2, '0'),
    value,
  }))
  // 分の選択肢を生成（ 1分～30分 なら 30分 / 31分～00分 なら 00分）
  const minuteOption = hour === 18 ? [0] : [0, 30]
  const minuteOptions: OptionItem<number>[] = minuteOption.map((value) => ({
    label: String(value).padStart(2, '0'),
    value,
  }))
  // 30分が選択されている状態で、18時が選択された時点で0分にリセット
  const lastReservationTime = (): void => {
    const lastReservationMinuteTime = 0
    if (selectedHour === 18) {
      setSelectedMinute(lastReservationMinuteTime)
    }
  }

  // ユーザーが時間と分を選択した際のイベント
  const changeNotification = useCallback(() => {
    if (onChanged) {
      onChanged(
        `${String(selectedHour).padStart(2, '0')}:${String(
          selectedMinute
        ).padStart(2, '0')}`
      )
    }
  }, [selectedHour, selectedMinute, onChanged])

  // プルダウンを開いた際に、selectedまでスクロールする処理（"分"は選択肢が少ないため対象外）
  const scrollDownToSelect = (selectedValue: number): void => {
    setTimeout(() => {
      const selectedElement = document.querySelector(
        `[data-year-value="${selectedValue}"]`
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
      }
    }, 0)
  }

  // 時間と分をクリックした際にプルダウン表示 & 時間はselected までスクロール
  const handleTimeClickHour = useCallback(() => {
    setHourItems(hourOptions.filter((x) => x.value >= minHour))
    setIsPickerOpenedHour(true)
    setIsPickerOpenedMinute(false)
    scrollDownToSelect(selectedHour)
  }, [minHour, setHourItems, selectedHour])

  const handleTimeClickMinute = useCallback(() => {
    const currentMinMinute = minHour === hour ? minMinute : 0
    setMinuteItems(minuteOptions.filter((x) => x.value >= currentMinMinute))
    lastReservationTime()
    setIsPickerOpenedMinute(true)
    setIsPickerOpenedHour(false)
  }, [hour, minute, minMinute, setMinuteItems])

  // 選択されたものを更新しプルダウンを閉じる処理
  const handleHourChanged = useCallback(
    (option): void => {
      setSelectedHour(option.value)
      setIsPickerOpenedHour(false)
      // 予約最小／分(minMinute)が 30分 の時に、選択された時間が
      // 予約最小／時(minHour) と一致したとき 分 を minMinute(30分) にリセットする
      minTime &&
        option.value === minHour &&
        minMinute === 30 &&
        setSelectedMinute(minMinute)
    },
    [minHour, selectedHour, isPickerOpenedHour]
  )
  const handleMinuteChanged = useCallback(
    (option): void => {
      setSelectedMinute(option.value)
      setIsPickerOpenedMinute(false)
    },
    [setSelectedMinute, isPickerOpenedMinute]
  )

  // 日付を切り替えたときにTimeの選択肢を生成する
  useEffect(() => {
    const currentMinHour = minHour > selectedHour ? minHour : selectedHour
    const currentMinMinute =
      minMinute > selectedMinute ? minMinute : selectedMinute
    props.onChanged &&
      props.onChanged(
        `${String(currentMinHour).padStart(2, '0')}:${String(
          currentMinMinute
        ).padStart(2, '0')}`
      )
    lastReservationTime()
    changeNotification()
    return
  }, [selectedHour, selectedMinute])

  useEffect(() => {
    if (minTime) {
      minMinute === 30 &&
        setMinuteItems([
          {
            label: '30',
            value: 30,
          },
        ])
      setSelectedHour(minHour)
      setSelectedMinute(minMinute)
      isPickerOpenedHour === true && handleTimeClickHour()
    } else {
      isPickerOpenedHour === true && handleTimeClickHour()
      isPickerOpenedMinute === true && handleTimeClickMinute()
    }
  }, [minTime])

  return (
    <ReservationTimeSelectionWrap>
      <TimeContainer>
        <TimeContainerContent onClick={handleTimeClickHour}>
          {selectedHour.toString().padStart(2, '0')}
        </TimeContainerContent>
        {isPickerOpenedHour && (
          <StyledPullDownPicker
            options={hourItems}
            selectedValue={selectedHour}
            handleValueChanged={handleHourChanged}
          />
        )}
      </TimeContainer>
      <Text>{'：'}</Text>
      <TimeContainer>
        <TimeContainerContent onClick={handleTimeClickMinute}>
          {selectedMinute.toString().padStart(2, '0')}
        </TimeContainerContent>
        {isPickerOpenedMinute && (
          <StyledPullDownPicker
            options={minuteItems}
            selectedValue={selectedMinute}
            handleValueChanged={handleMinuteChanged}
          />
        )}
      </TimeContainer>
    </ReservationTimeSelectionWrap>
  )
}
export default React.memo(TimePicker)

const ReservationTimeSelectionWrap = styled.div`
  display: flex;
  align-items: center;
`
const TimeContainer = styled.div`
  width: auto;
  position: relative;
`
/** ルートのスタイル定義 */
const TimeContainerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 53px;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 18px;
  border: 2px solid #707070;
  // プルダウン三角アイコン
  position: relative;
  &::before {
    content: '';
    border-right: 7px solid transparent;
    border-bottom: 13px solid #333;
    border-left: 7px solid transparent;
    position: absolute;
    right: 3px;
    top: 25%;
    transform: translate(0 -50%);
  }
  &::after {
    content: '';
    border-right: 7px solid transparent;
    border-top: 13px solid #333;
    border-left: 7px solid transparent;
    position: absolute;
    right: 3px;
    bottom: 25%;
    transform: translate(0 -50%);
  }
`
const StyledPullDownPicker = styled(PullDownPicker)`
  margin: 0;
  padding: 5px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  max-height: 130px;
  background-color: #fff;
  border: #707070 1px solid;
  display: flex;
  flex-direction: column;
  line-height: 36px;
  overflow-y: scroll;
`
